import { PropsWithChildren, useEffect } from 'react';

import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { MarkdownText } from '@/components/atoms/MarkdownText/MarkdownText';
import {
  SidebarWrapper,
  SidebarWrapperFooter,
  SidebarWrapperOverflowContainer,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { useResponsiveSidebarClose } from '@/components/organisms/Sidebar/ResponsiveSidebarPanel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { selectBrainId } from '@/redux/session/selectors';
import { selectSidebarMeta } from '@/redux/sidebar/selectors';
import { timeFromNow } from '@/util/dateUtils';

import { ImprovementActions } from './ImprovementActions';
import { useImprovements } from '../hooks/useImprovements';
import {
  SEVERITY_BADGE_FLAVOUR,
  SEVERITY_TRANSLATION_KEYS,
  STATUS_BADGE_FLAVOUR,
  STATUS_TRANSLATION_KEYS,
} from '../types';
import { humanizeKey, formatImpactValue } from '../utils';

// Omni writes content/reasoning as markdown (headings, lists, `code`, **bold**);
// typography for the rendered elements, since the Tailwind preflight resets them.
const MARKDOWN_CLASSES =
  'text-sm leading-5 [&_a]:text-informational [&_a]:underline [&_code]:rounded [&_code]:bg-bg-muted-50 [&_code]:px-1 [&_code]:font-mono [&_code]:text-[13px] [&_em]:italic [&_h1]:mt-3 [&_h1]:mb-1 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-bg-muted-50 [&_pre]:p-2 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5';

const DetailSection = ({ label, children }: PropsWithChildren<{ label: string }>) => (
  <div className="flex flex-col gap-1.5">
    <h4 className="text-sm leading-5 font-semibold text-foreground">{label}</h4>
    {children}
  </div>
);

/**
 * Generic, type-agnostic detail panel (D7): renders whatever the improvement
 * carries — no diff viewer or session links in v0.
 */
export const ImprovementDetails = () => {
  const { t } = useTranslation();
  const close = useResponsiveSidebarClose();
  const brainId = useSelector(selectBrainId);
  const meta = useSelector(selectSidebarMeta);

  const { improvements, isLoading } = useImprovements(brainId);
  const improvement = improvements.find((i) => i.improvement_id === meta?.improvementId);

  // The selected row can vanish from the cache (e.g. superseded server-side);
  // close the panel instead of showing an empty shell.
  const isMissing = !isLoading && !improvement;
  useEffect(() => {
    if (isMissing) {
      close();
    }
  }, [isMissing, close]);

  if (!improvement) {
    return null;
  }

  const impactEntries = Object.entries(improvement.impact ?? {});

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-6 font-semibold text-foreground">
          {t('improvements.details')}
        </h2>
        <Button variant="ghost-neutral" size="icon-sm" onClick={close}>
          <XIcon size={16} />
        </Button>
      </SidebarWrapperTitle>

      <SidebarWrapperOverflowContainer>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge type="secondary" flavour={SEVERITY_BADGE_FLAVOUR[improvement.severity]}>
                {t(SEVERITY_TRANSLATION_KEYS[improvement.severity])}
              </Badge>
              <Badge type="outline" flavour="neutral">
                {humanizeKey(improvement.improvement_type)}
              </Badge>
              <Badge type="secondary" flavour={STATUS_BADGE_FLAVOUR[improvement.status]}>
                {t(STATUS_TRANSLATION_KEYS[improvement.status])}
              </Badge>
            </div>
            <h3 className="text-lg leading-6 font-semibold text-foreground">{improvement.title}</h3>
            <p className="text-sm leading-5 text-foreground-muted">{improvement.summary}</p>
          </div>

          <DetailSection label={t('improvements.content_label')}>
            <MarkdownText
              markdown={improvement.content}
              className={cn(MARKDOWN_CLASSES, 'text-foreground')}
            />
          </DetailSection>

          {improvement.reasoning && (
            <DetailSection label={t('improvements.reasoning_label')}>
              <MarkdownText
                markdown={improvement.reasoning}
                className={cn(MARKDOWN_CLASSES, 'text-foreground-muted')}
              />
            </DetailSection>
          )}

          {impactEntries.length > 0 && (
            <DetailSection label={t('improvements.impact_label')}>
              <div className="flex flex-col gap-1">
                {impactEntries.map(([key, value]) => (
                  <div key={key} className="flex items-baseline justify-between gap-4 text-sm">
                    <span className="text-foreground-muted">{humanizeKey(key)}</span>
                    <span className="text-right font-medium break-all text-foreground">
                      {formatImpactValue(value)}
                    </span>
                  </div>
                ))}
              </div>
            </DetailSection>
          )}

          {improvement.feedback && (
            <DetailSection label={t('improvements.feedback_label')}>
              <p className="text-sm leading-5 whitespace-pre-wrap text-foreground-muted">
                {improvement.feedback}
              </p>
            </DetailSection>
          )}

          <p className="text-xs text-foreground-muted">
            {t('common.created')} {timeFromNow(improvement.created)}
          </p>
        </div>

        <SidebarWrapperFooter>
          <ImprovementActions improvement={improvement} onDone={close} />
        </SidebarWrapperFooter>
      </SidebarWrapperOverflowContainer>
    </SidebarWrapper>
  );
};
